import {
  useCreateOrganizationPricingHoldbacksMutation,
  useDeleteOrganizationPricingHoldbacksMutation,
} from '@/services/pricing';
import { useGetUtilitiesQuery } from '@/services/utilities';
import { StandardHoldbackSelfServiceClientData } from '@/types/Holdbacks';
import {
  Box,
  Button,
  FormikCheckboxInput,
  Modal,
  SelectInput,
  SimulatedEventPayloadType,
  TextInput,
  toast,
} from '@palmetto/palmetto-components';
import { Field, Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { useCallback, useMemo } from 'react';
import * as yup from 'yup';

import { ProgramType } from 'types';

type HoldbackFormValuesType = {
  amount: number | string;
  states: { label: string; value: string }[];
  confirmation: boolean;
};

interface EditStandardHoldbackModalProps {
  programType: ProgramType;
  alias: string;
  isOpen: boolean;
  handleClose: () => void;
  activeHoldbacks: StandardHoldbackSelfServiceClientData[];
}

const editHoldbackSchema = yup.object({
  amount: yup
    .number()
    .transform((_value, originalValue) => {
      if (originalValue === '' || originalValue == null) {
        return undefined;
      }
      const parsed = parseFloat(originalValue);
      return isNaN(parsed) ? undefined : parsed;
    })
    .required('Amount is a required field')
    .min(0, 'Value must be zero or greater')
    .max(0.5, 'Holdback amount must be less than max allowance'),
  states: yup.array().min(1, 'At least one state must be selected'),
});

const confirmationBoxChildGap = { base: 'lg', desktop: 'xl' } as const;
const amountInputProps = { step: 0.01 };

interface FormContentProps {
  isOpen: boolean;
  stateOptions: { label: string; value: string }[];
  handleClose: () => void;
}

const FormContent = ({ isOpen, stateOptions, handleClose }: FormContentProps) => {
  const { isSubmitting, setFieldValue, touched, handleBlur, resetForm, submitForm, values, errors } =
    useFormikContext<HoldbackFormValuesType>();

  const handleDismiss = useCallback(() => {
    if (!isSubmitting) {
      resetForm();
      handleClose();
    }
  }, [isSubmitting, resetForm, handleClose]);

  const handleAmountChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFieldValue('amount', event.target.value);
    },
    [setFieldValue],
  );

  const handleAmountBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const formatted = parseFloat(event.target.value || '0').toFixed(2);
      setFieldValue('amount', formatted);
      handleBlur(event);
    },
    [setFieldValue, handleBlur],
  );

  const handleStatesChange = useCallback(
    (event: SimulatedEventPayloadType) => setFieldValue('states', event.target.value),
    [setFieldValue],
  );

  const handleConfirmationChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setFieldValue('confirmation', e.target.checked),
    [setFieldValue],
  );

  const isArchiveMode = values.amount !== '' && Number(values.amount) === 0;

  return (
    <Form>
      <Modal isOpen={isOpen} maxWidth="4xl" ariaLabelledBy="editStandardHoldbackModal" onDismiss={handleDismiss}>
        <Modal.Header id="editStandardHoldbackModal" title="Edit Holdback" onDismiss={handleDismiss} />
        <Modal.Body background="secondary" childGap="lg">
          <Box gap="lg">
            <Box gap="2xs">
              <TextInput
                type="number"
                label="Amount"
                name="amount"
                helpText="Max allowance is $0.50"
                id="amount"
                value={values.amount}
                isDisabled={isSubmitting}
                onChange={handleAmountChange}
                prefix="$"
                suffix="/watt"
                error={touched.amount && (errors.amount as string)}
                onBlur={handleAmountBlur}
                inputProps={amountInputProps}
              />
            </Box>
            <SelectInput
              id="states"
              name="states"
              label="State(s)"
              value={values.states}
              onChange={handleStatesChange}
              options={stateOptions}
              onBlur={handleBlur}
              isDisabled={isSubmitting}
              error={touched.states && (errors.states as string)}
              menuPortalTarget={document.body}
              isMulti
            />
            <Box childGap={confirmationBoxChildGap} direction="row">
              <Field
                label="I understand and agree that any changes made to holdbacks will apply to accounts that receive a quote following any holdback change. Any account that has already had a contract sent will not be impacted."
                name="confirmation"
                id="confirmation"
                component={FormikCheckboxInput}
                onChange={handleConfirmationChange}
              />
            </Box>
          </Box>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            tone="neutral"
            isLoading={isSubmitting}
            isDisabled={isSubmitting}
            onClick={handleDismiss}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            tone={isArchiveMode ? 'danger' : 'primary'}
            isLoading={isSubmitting}
            isDisabled={isSubmitting || !values.confirmation}
            type="submit"
            onClick={submitForm}
          >
            {isArchiveMode ? 'Archive' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Form>
  );
};

export const EditStandardHoldbackModal = ({
  programType,
  alias,
  isOpen,
  handleClose,
  activeHoldbacks,
}: EditStandardHoldbackModalProps) => {
  const [createOrganizationPricingHoldbackMutation] = useCreateOrganizationPricingHoldbacksMutation();
  const [deleteOrganizationPricingHoldbackMutation] = useDeleteOrganizationPricingHoldbacksMutation();

  const filter = [{ id: 'programType', operation: 'Equal', selectedValues: [programType] }];
  const { data: utilities = [] } = useGetUtilitiesQuery(
    programType ? { advancedFilters: JSON.stringify(filter) } : {},
    { skip: !programType },
  );

  const stateOptions = useMemo(() => {
    const uniqueStates = new Set(utilities.map((l) => l.state));
    return Array.from(uniqueStates)
      .toSorted((a, b) => (String(a) < String(b) ? -1 : String(a) > String(b) ? 1 : 0))
      .map((state) => ({ label: state, value: state }));
  }, [utilities]);

  const onSubmit = useCallback(
    async (
      formValues: HoldbackFormValuesType,
      { setSubmitting, resetForm }: FormikHelpers<HoldbackFormValuesType>,
    ) => {
      const isArchiveMode = formValues.amount !== '' && Number(formValues.amount) === 0;

      try {
        const selectedStates = formValues.states.map((state) => state.value);

        if (isArchiveMode) {
          const holdbacksToArchive = activeHoldbacks.filter((holdback) => selectedStates.includes(holdback.state));

          if (holdbacksToArchive.length === 0) {
            toast.success('No active holdbacks found for the selected state(s)');
            handleClose();
            resetForm();
            return;
          }

          const results = await Promise.allSettled(
            holdbacksToArchive.map((holdback) =>
              deleteOrganizationPricingHoldbackMutation({
                alias,
                id: holdback.id.toString(),
                programType,
              }).unwrap(),
            ),
          );

          const succeeded = results.filter((r) => r.status === 'fulfilled').length;
          const failed = results.filter((r) => r.status === 'rejected').length;

          if (failed > 0) {
            toast.error(`Archived ${succeeded}, failed to archive ${failed} holdback${failed > 1 ? 's' : ''}`);
          } else {
            toast.success(`Archived ${succeeded} holdback${succeeded > 1 ? 's' : ''} successfully`);
          }
          handleClose();
          resetForm();
          return;
        }

        await createOrganizationPricingHoldbackMutation({
          alias,
          data: { amount: formValues.amount, states: selectedStates, programType },
        }).unwrap();
        handleClose();
        toast.success('Holdback amount has been updated');
        resetForm();
      } catch (error: any) {
        console.error(error);
        const message =
          error?.data?.message ?? (isArchiveMode ? 'Could not archive holdbacks' : 'Could not update holdback');
        toast.error(message);
      } finally {
        setSubmitting(false);
      }
    },
    [
      alias,
      programType,
      activeHoldbacks,
      createOrganizationPricingHoldbackMutation,
      deleteOrganizationPricingHoldbackMutation,
      handleClose,
    ],
  );

  return (
    <Formik<HoldbackFormValuesType>
      initialValues={{ amount: '', states: [], confirmation: false }}
      validationSchema={editHoldbackSchema}
      onSubmit={onSubmit}
      enableReinitialize
      validateOnBlur
    >
      <FormContent isOpen={isOpen} stateOptions={stateOptions} handleClose={handleClose} />
    </Formik>
  );
};
